import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES, YearBg } from "../../assets/asset";
import CallAllTypeFromHere from "../CallAllTypeFromHere/CallAllTypeFromHere";
import { SkeletonVideo } from "../../utils/SkeletonLoader";
import CustomButton from "../../assets/buttons/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { setActiveYear } from "../../store/slice/scrollPositionSlice";
import { setMuteValue } from "../../store/slice/muteSlice";
import { Loader2 } from "lucide-react";
import {
  readContinueLearning,
  saveContinueLearning,
} from "../../utils/continueLearning";
import { listQuestionsV2 } from "../../services/questionV2.service";
import { getLessonV2 } from "../../services/lessonV2.service";

const FoundationLessonCom = () => {
  const { token, user } = useSelector((state) => state.user);
  const { olderValueWas } = useSelector((state) => state.mute);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const savedContinueLearning = readContinueLearning(user);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoShared, setVideoShared] = useState("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let storedLessonContext = {};
  try {
    storedLessonContext = JSON.parse(
      sessionStorage.getItem("lessonContext") || "{}"
    );
  } catch (_) {
    storedLessonContext = {};
  }

  const lessonId = Number(
    location.state?.lessonId ??
      searchParams.get("lessonId") ??
      storedLessonContext.lessonId
  );
  const yearId = Number(
    location.state?.yearId ??
      searchParams.get("yearId") ??
      storedLessonContext.yearId
  );
  const colorTheme =
    location.state?.colorTheme ?? storedLessonContext.colorTheme ?? "#000000";
  const lessonDisplayName =
    location.state?.lessonDisplayName ??
    searchParams.get("lessonDisplayName") ??
    storedLessonContext.lessonDisplayName ??
    "";
  const isV2 = Boolean(
    location.state?.isV2 ?? storedLessonContext.isV2 ?? false
  );
  const v2Video = String(
    location.state?.v2Video ?? storedLessonContext.v2Video ?? ""
  );
  const isMatchingSavedLesson =
    Number(savedContinueLearning?.lessonId) === lessonId &&
    Number(savedContinueLearning?.yearId) === yearId;
  const resumeQuestionIndex = isMatchingSavedLesson
    ? Number(savedContinueLearning?.questionIndex) || 0
    : 0;
  const shouldResumeQuestions =
    isMatchingSavedLesson && savedContinueLearning?.phase === "questions";
  const [showTraceCom, setShowTraceCom] = useState(shouldResumeQuestions);
  const isLessonContextValid =
    Number.isFinite(lessonId) &&
    lessonId > 0 &&
    Number.isFinite(yearId) &&
    yearId > 0;

  const fetchData = useCallback(async () => {
    if (!token || !isLessonContextValid) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      if (isV2) {
        // V2 lesson: fetch questions via getLessonV2 with includeQuestions
        const v2LessonResult = await getLessonV2(lessonId, true).catch((err) => {
          console.warn("[LessonV2] getLessonV2 failed:", err);
          return { isSuccessful: false, data: null };
        });

        const v2Questions =
          v2LessonResult?.isSuccessful && v2LessonResult.data?.questions
            ? v2LessonResult.data.questions
            : [];

        const v2Formatted = v2Questions
          .filter((q) => String(q?.status || "").trim().toLowerCase() !== "archived")
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((q) => ({
            pk: `v2-${q.id}`,
            isV2: true,
            fields: {
              questionType: "QuestionV2Renderer",
              question: q.question || "",
              lession: q.lessonId,
              v2SummaryData: q,
              imageList: [],
              questionimagelist: [],
              questionaudio: [],
              answer: [],
              answear: [],
              explaination: q.explanation || "",
              complexity: q.difficulty || "",
              QuestionId: String(q.id),
            },
          }));

        const result = { data: v2Formatted };
        setLessonData(result);

        // Use V2 lesson video
        const videoUrl = v2Video || "";
        setVideoShared(videoUrl);

        if (shouldResumeQuestions || !videoUrl) {
          setShowTraceCom(true);
        }
      } else {
        // Legacy lesson: fetch legacy questions and V2 questions in parallel
        const [legacyResponse, v2Result] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_BASE_URL}api/questionoflession`,
            {
              method: "POST",
              headers: {
                Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
                "Content-Type": "application/json",
                jwt: token,
              },
              body: JSON.stringify({ LessionId: lessonId }),
            }
          ),
          listQuestionsV2(lessonId).catch((err) => {
            console.warn("[QuestionV2] Parallel fetch failed, continuing with legacy only:", err);
            return { isSuccessful: false, data: [] };
          }),
        ]);

        if (!legacyResponse.ok)
          throw new Error(`HTTP error! Status: ${legacyResponse.status}`);

        const result = await legacyResponse.json();
        if (
          result?.status === "Fail" &&
          (result?.status_code === 304 ||
            result?.message === "jwt token is not valid")
        ) {
          navigate("/login", { replace: true });
          return;
        }

        // Wrap V2 questions into the legacy shape and append them
        if (v2Result?.isSuccessful && Array.isArray(v2Result.data) && v2Result.data.length > 0) {
          const v2Formatted = v2Result.data
            .filter((q) => String(q?.status || "").trim().toLowerCase() !== "archive")
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((q) => ({
              pk: `v2-${q.id}`,
              isV2: true,
              fields: {
                questionType: "QuestionV2Renderer",
                question: q.question || "",
                lession: q.lessonId,
                v2SummaryData: q,
                imageList: [],
                questionimagelist: [],
                questionaudio: [],
                answer: [],
                answear: [],
                explaination: q.explanation || "",
                complexity: q.difficulty || "",
                QuestionId: String(q.id),
              },
            }));

          result.data = [...(result.data || []), ...v2Formatted];
        }

        setLessonData(result);

        const videoUrl = result?.videourl?.[0]?.fields?.videourl || "";
        setVideoShared(videoUrl);

        if (shouldResumeQuestions || result?.isTestLesson || !videoUrl) {
          setShowTraceCom(true);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [token, lessonId, isLessonContextValid, navigate, shouldResumeQuestions, isV2, v2Video]);

  useEffect(() => {
    if (!isLessonContextValid) {
      navigate("/lessonDashboard", { replace: true });
      return;
    }
    if (token) fetchData();
  }, [token, fetchData, isLessonContextValid, navigate]);

  useEffect(() => {
    if (!Number.isFinite(yearId) || yearId <= 0) return;
    dispatch(setActiveYear(yearId));
    dispatch(setMuteValue(true));

    return () => {
      dispatch(setMuteValue(olderValueWas));
    };
  }, [dispatch, olderValueWas, yearId]);

  useEffect(() => {
    if (!isLessonContextValid || showTraceCom) return;

    saveContinueLearning(user, {
      lessonId,
      yearId,
      colorTheme,
      lessonDisplayName: String(lessonDisplayName || ""),
      phase: "video",
      questionIndex: 0,
    });
  }, [
    colorTheme,
    isLessonContextValid,
    lessonDisplayName,
    lessonId,
    showTraceCom,
    user,
    yearId,
  ]);

  const handleVideoEnd = () => {
    saveContinueLearning(user, {
      lessonId,
      yearId,
      colorTheme,
      lessonDisplayName: String(lessonDisplayName || ""),
      phase: "questions",
      questionIndex: resumeQuestionIndex,
    });
    setShowTraceCom(true);
  };

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-white">
        {/* <Lottie className="w-48 h-48" pbadges /> */}
        <Loader2 className="h-12 w-12 animate-spin text-gray-600" />
      </div>
    );

  if (error) return <div>Error: {error}</div>;

  console.log({ isVideoLoaded });

  return (
    <div
      className={`lesson-video-screen relative min-h-screen flex flex-col items-center bg-blue-50 font-mali overflow-y-auto ${
        isVideoLoaded ? "" : "bg-transparent"
      }`}
      style={{
        backgroundImage: `url(${YearBg[yearId - 1] || GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        width: "100%",
        minHeight: "100%",
      }}
    >
      {/* Background dim layer only while video is active */}
      {!showTraceCom && (
        <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
      )}

      {/* Video Phase */}
      {!showTraceCom &&
        lessonData &&
        !lessonData.isTestLesson &&
        videoShared && (
          <div className="lesson-video-stage relative z-10 flex min-h-screen w-full flex-col items-center justify-center px-4 py-8 sm:px-6">
            {!isVideoLoaded && (
              <div className="lesson-video-skeleton w-full max-w-5xl overflow-hidden rounded-3xl">
                <SkeletonVideo />
              </div>
            )}
            <video
              className={`lesson-video-player w-full max-w-5xl h-auto max-h-[70vh] rounded-3xl bg-gray-900 object-contain shadow-2xl ${
                !isVideoLoaded ? "hidden" : ""
              }`}
              src={videoShared}
              controls
              autoPlay
              onEnded={handleVideoEnd}
              onLoadedData={handleVideoLoad}
            />

            <div className="lesson-video-actions mt-4 flex w-full max-w-5xl justify-end">
              <CustomButton
                text={"Continue"}
                onClick={handleVideoEnd}
                disabled={false}
                isShaking={false}
                isIcon={true}
                type={"correct"}
              />
            </div>
          </div>
        )}

      {/* CallAllType Phase */}
      {showTraceCom && lessonData && (
        <div className="relative z-10 w-full">
          <CallAllTypeFromHere
            lessonData={lessonData.data}
            yearId={yearId}
            video={videoShared}
            colorTheme={colorTheme}
            lessonId={lessonId}
            lessonDisplayName={lessonDisplayName}
            initialQuestionIndex={resumeQuestionIndex}
            onProgressChange={(questionIndex) => {
              saveContinueLearning(user, {
                lessonId,
                yearId,
                colorTheme,
                lessonDisplayName: String(lessonDisplayName || ""),
                phase: "questions",
                questionIndex,
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FoundationLessonCom;
